import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Users, CheckCircle2, Globe, Route, Search, Wifi, MapPin, Monitor,
  Smartphone, Clock, Eye, ShoppingCart, ArrowLeft, ArrowRight, Download,
  X, RefreshCw, Filter, ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
  ArrowUpDown, Megaphone, RotateCcw, ChevronDown, ChevronUp, Tablet,
  Ban, ShieldOff,
} from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtDuration, flagEmoji, fmtTime, COMMON_COUNTRIES } from "@/lib/regie";
import { ALL_COUNTRIES, COUNTRY_NAME_BY_CODE } from "@/lib/countries";
import { VisitorJourney, BlockedBadge, BotBadge, MobilityBadge, RiskPill } from "@/pages/admin/SuperRegie";

// Options du filtre pays : pays fréquents en tête, puis la liste complète
// (ISO 3166-1) pour que n'importe quel pays (Maroc, Algérie…) soit filtrable.
const COMMON_CODES = new Set(COMMON_COUNTRIES.map((c) => c.code));
const OTHER_COUNTRIES = ALL_COUNTRIES.filter((c) => !COMMON_CODES.has(c.code));
function CountryOptions() {
  return (
    <>
      <optgroup label="Fréquents">
        {COMMON_COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{flagEmoji(c.code)} {c.label}</option>
        ))}
      </optgroup>
      <optgroup label="Tous les pays">
        {OTHER_COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{flagEmoji(c.code)} {c.name}</option>
        ))}
      </optgroup>
    </>
  );
}

const PAGE_SIZES = [20, 50, 100, 200];
const DAYS_PRESETS = [
  { v: 1, l: "24h" }, { v: 7, l: "7 j" }, { v: 30, l: "30 j" },
  { v: 90, l: "90 j" }, { v: 180, l: "6 mois" }, { v: 365, l: "1 an" },
];
const DEVICE_PRESETS = ["Ordinateur", "Mobile", "Tablette"];
const BROWSER_PRESETS = ["Chrome", "Safari", "Firefox", "Edge", "Opera"];
const OS_PRESETS = ["Windows", "macOS", "Linux", "iOS", "Android"];

const emptyFilters = {
  days: 30, conv: "all", country: "", q: "", bots: "exclude",
  device: "", browser: "", os: "",
  has_cart: "any", has_products: "any",
  min_pages: "", min_duration_sec: "",
  sort: "last_seen", order: "desc",
};

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-white rounded-sm border border-stone p-4">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.14em] text-ash">{label}</p>
      <Icon className="h-4 w-4 text-copper" strokeWidth={1.5} />
    </div>
    <p className="font-serif text-2xl md:text-3xl text-ink mt-2">{value}</p>
    {sub && <p className="text-[11px] text-ash mt-1">{sub}</p>}
  </div>
);

const TinyBar = ({ label, count, total }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-graphite w-16 truncate shrink-0">{label}</span>
    <div className="flex-1 bg-sand h-1.5 rounded-full overflow-hidden">
      <div className="h-full bg-gold" style={{ width: `${total ? (count / total) * 100 : 0}%` }} />
    </div>
    <span className="text-ink font-medium w-6 text-right">{count}</span>
  </div>
);

const SortHeader = ({ label, field, sort, order, onSort, align = "left" }) => {
  const active = sort === field;
  const Icon = active ? (order === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
      className={`flex items-center gap-1 text-xs uppercase tracking-[0.12em] hover:text-ink transition-colors ${active ? "text-ink" : "text-ash"} ${align === "right" ? "ml-auto" : ""}`}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
};

export default function SuperRegieOrganicVisitors() {
  const { user } = useAuth();
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(0); // 0 = off, secondes sinon
  const [activeTab, setActiveTab] = useState("visitors"); // "visitors" | "ip-stats"
  const [ipStatsDays, setIpStatsDays] = useState(7);
  const [ipStats, setIpStats] = useState(null);
  const [ipStatsLoading, setIpStatsLoading] = useState(false);
  const [blockingIp, setBlockingIp] = useState(null); // ip string currently being blocked
  const [ipSearchIp, setIpSearchIp] = useState("");
  const [ipSearchCountry, setIpSearchCountry] = useState("");
  const [ipSort, setIpSort] = useState("recent"); // "recent" (en direct) | "sessions" (plus actives)
  const [manualBlockIp, setManualBlockIp] = useState("");
  const [manualBlocking, setManualBlocking] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr(false);
    const params = {
      days: applied.days,
      limit: pageSize,
      skip: page * pageSize,
      sort: applied.sort,
      order: applied.order,
    };
    if (applied.conv === "yes") params.converted = true;
    if (applied.conv === "no") params.converted = false;
    params.bots = applied.bots || "exclude";
    if (applied.country) params.country_code = applied.country.toUpperCase();
    if (applied.q) params.search = applied.q;
    if (applied.device) params.device = applied.device;
    if (applied.browser) params.browser = applied.browser;
    if (applied.os) params.os = applied.os;
    if (applied.has_cart === "yes") params.has_cart = true;
    if (applied.has_cart === "no") params.has_cart = false;
    if (applied.has_products === "yes") params.has_products = true;
    if (applied.has_products === "no") params.has_products = false;
    if (applied.min_pages) params.min_pages = Number(applied.min_pages);
    if (applied.min_duration_sec) params.min_duration_ms = Number(applied.min_duration_sec) * 1000;

    api.get("/superadmin/regie/organic-visitors", { params })
      .then(({ data }) => setData(data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [applied, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const loadIpStats = useCallback(() => {
    setIpStatsLoading(true);
    const params = { days: ipStatsDays, sort: ipSort };
    if (ipSearchIp) params.search_ip = ipSearchIp;
    if (ipSearchCountry) params.country_code = ipSearchCountry;
    api.get("/superadmin/regie/ip-stats", { params })
      .then(({ data }) => setIpStats(data))
      .catch(() => {})
      .finally(() => setIpStatsLoading(false));
  }, [ipStatsDays, ipSearchIp, ipSearchCountry, ipSort]);

  useEffect(() => {
    if (activeTab === "ip-stats") loadIpStats();
  }, [activeTab, loadIpStats]);

  const toggleIpBlock = async (row) => {
    setBlockingIp(row.ip);
    try {
      if (row.is_blocked) {
        await api.delete(`/superadmin/ip-blocks/${encodeURIComponent(row.ip)}`);
        toast.success(`IP ${row.ip} débloquée`);
      } else {
        await api.post("/superadmin/ip-block", { ip: row.ip, reason: "Bloqué depuis les stats IP" });
        toast.success(`IP ${row.ip} bloquée`);
      }
      loadIpStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors du blocage/déblocage de l'IP");
    } finally {
      setBlockingIp(null);
    }
  };

  const manualBlock = async () => {
    const ip = manualBlockIp.trim();
    if (!ip) return;
    setManualBlocking(true);
    try {
      await api.post("/superadmin/ip-block", { ip, reason: "Blocage manuel depuis Stats IP" });
      toast.success(`IP ${ip} bloquée`);
      setManualBlockIp("");
      if (activeTab === "ip-stats") loadIpStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors du blocage de l'IP");
    } finally {
      setManualBlocking(false);
    }
  };

  // Auto-refresh : recharge l'onglet actif (visiteurs OU stats IP)
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const tick = () => (activeTab === "ip-stats" ? loadIpStats() : load());
    const id = setInterval(tick, autoRefresh * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, activeTab, load, loadIpStats]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setApplied(filters);
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setApplied(emptyFilters);
    setPage(0);
  };

  const onSort = (field) => {
    const nextOrder = applied.sort === field && applied.order === "desc" ? "asc" : "desc";
    setApplied((f) => ({ ...f, sort: field, order: nextOrder }));
    setFilters((f) => ({ ...f, sort: field, order: nextOrder }));
    setPage(0);
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      params.set("days", String(applied.days));
      params.set("sort", applied.sort);
      params.set("order", applied.order);
      if (applied.conv === "yes") params.set("converted", "true");
      if (applied.conv === "no") params.set("converted", "false");
      if (applied.country) params.set("country_code", applied.country.toUpperCase());
      if (applied.q) params.set("search", applied.q);
      if (applied.device) params.set("device", applied.device);
      if (applied.browser) params.set("browser", applied.browser);
      if (applied.os) params.set("os", applied.os);
      if (applied.has_cart === "yes") params.set("has_cart", "true");
      if (applied.has_cart === "no") params.set("has_cart", "false");
      if (applied.has_products === "yes") params.set("has_products", "true");
      if (applied.has_products === "no") params.set("has_products", "false");
      if (applied.min_pages) params.set("min_pages", String(applied.min_pages));
      if (applied.min_duration_sec) params.set("min_duration_ms", String(Number(applied.min_duration_sec) * 1000));
      const res = await api.get(`/superadmin/regie/organic-visitors.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `visiteurs-organiques-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch (e) {
      toast.error("Erreur lors de l'export CSV.");
    }
  };

  const list = data?.visitors || [];
  const agg = data?.aggregates || {
    total_revenue: 0, total_pages: 0, avg_duration_ms: 0, total_cart_adds: 0,
    top_countries: [], top_devices: [], top_browsers: [], top_referrers: [],
  };
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total > 0 ? page * pageSize + 1 : 0;
  const pageEnd = Math.min(total, (page + 1) * pageSize);
  const canPrev = page > 0;
  const canNext = (page + 1) * pageSize < total;

  const activeChips = useMemo(() => {
    const chips = [];
    if (applied.conv !== "all") chips.push({ k: "conv", label: applied.conv === "yes" ? "Convertis" : "Non convertis" });
    if (applied.bots && applied.bots !== "exclude") chips.push({ k: "bots", label: applied.bots === "only" ? "Robots uniquement" : "Humains + robots" });
    if (applied.country) chips.push({ k: "country", label: `Pays ${COUNTRY_NAME_BY_CODE[applied.country.toUpperCase()] || applied.country}` });
    if (applied.device) chips.push({ k: "device", label: applied.device });
    if (applied.browser) chips.push({ k: "browser", label: applied.browser });
    if (applied.os) chips.push({ k: "os", label: applied.os });
    if (applied.has_cart !== "any") chips.push({ k: "has_cart", label: applied.has_cart === "yes" ? "Avec panier" : "Sans panier" });
    if (applied.has_products !== "any") chips.push({ k: "has_products", label: applied.has_products === "yes" ? "A vu des produits" : "Aucun produit vu" });
    if (applied.min_pages) chips.push({ k: "min_pages", label: `≥ ${applied.min_pages} pages` });
    if (applied.min_duration_sec) chips.push({ k: "min_duration_sec", label: `≥ ${applied.min_duration_sec}s` });
    if (applied.q) chips.push({ k: "q", label: `« ${applied.q} »` });
    return chips;
  }, [applied]);

  const removeChip = (k) => {
    const patch =
      k === "conv" ? { conv: "all" } :
      k === "bots" ? { bots: "exclude" } :
      k === "country" ? { country: "" } :
      k === "device" ? { device: "" } :
      k === "browser" ? { browser: "" } :
      k === "os" ? { os: "" } :
      k === "has_cart" ? { has_cart: "any" } :
      k === "has_products" ? { has_products: "any" } :
      k === "min_pages" ? { min_pages: "" } :
      k === "min_duration_sec" ? { min_duration_sec: "" } :
      k === "q" ? { q: "" } : {};
    const next = { ...applied, ...patch };
    setApplied(next);
    setFilters(next);
    setPage(0);
  };

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  return (
    <div data-testid="super-regie-organic-visitors">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <Link to="/admin/regie" className="text-xs uppercase tracking-[0.15em] text-ash hover:text-ink flex items-center gap-2 mb-3">
            <ArrowLeft className="h-3.5 w-3.5" /> Régie Publicitaire
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-gold" /> Super Admin · Régie
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Visiteurs organiques</h1>
          <p className="text-graphite mt-2 max-w-xl">
            Tous les visiteurs arrivés sans campagne attribuée : trafic direct, référents, SEO. Filtrez, exportez, plongez dans le parcours de chaque session.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="auto-refresh"
            value={autoRefresh}
            onChange={(e) => setAutoRefresh(Number(e.target.value))}
            className="text-xs uppercase tracking-[0.1em] border border-stone rounded-sm px-3 py-2 bg-white text-graphite"
            title="Auto-refresh"
          >
            <option value={0}>Auto-refresh : off</option>
            <option value={10}>Toutes les 10s</option>
            <option value={30}>Toutes les 30s</option>
            <option value={60}>Toutes les 1 min</option>
            <option value={300}>Toutes les 5 min</option>
          </select>
          <button
            data-testid="refresh-btn"
            onClick={() => (activeTab === "ip-stats" ? loadIpStats() : load())}
            className="p-2 border border-stone bg-white text-graphite hover:bg-sand rounded-sm"
            title="Rafraîchir"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            data-testid="export-csv"
            onClick={exportCsv}
            className="flex items-center gap-2 bg-ink text-cream px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors rounded-sm"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-stone mb-6">
        <button
          onClick={() => setActiveTab("visitors")}
          className={`px-5 py-2.5 text-xs uppercase tracking-[0.14em] border-b-2 transition-colors ${activeTab === "visitors" ? "border-gold text-ink font-medium" : "border-transparent text-ash hover:text-ink"}`}
        >
          Visiteurs
        </button>
        <button
          onClick={() => setActiveTab("ip-stats")}
          className={`px-5 py-2.5 text-xs uppercase tracking-[0.14em] border-b-2 transition-colors ${activeTab === "ip-stats" ? "border-gold text-ink font-medium" : "border-transparent text-ash hover:text-ink"}`}
        >
          Stats IP
        </button>
      </div>

      {activeTab === "visitors" && (
      <div>

      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat testid="kpi-total" icon={Users} label="Total organique" value={total} sub={`Sur ${applied.days} jours`} />
        <Stat testid="kpi-converted" icon={CheckCircle2} label="Convertis" value={data?.converted || 0} sub={`CA ${eur(agg.total_revenue || 0)}`} />
        <Stat testid="kpi-pages" icon={Eye} label="Pages vues" value={agg.total_pages || 0} sub={`Durée moy. ${fmtDuration(agg.avg_duration_ms || 0)}`} />
        <Stat testid="kpi-cart" icon={ShoppingCart} label="Ajouts panier" value={agg.total_cart_adds || 0} sub={`${agg.total_products_views || 0} vues produit`} />
      </div>

      {/* Top-lists synth */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-stone rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ash mb-3 flex items-center gap-1"><Globe className="h-3 w-3" /> Top pays</p>
          <div className="space-y-2">
            {agg.top_countries.slice(0, 5).map((c) => (
              <TinyBar key={c.key} label={`${flagEmoji(c.key)} ${c.key}`} count={c.count} total={total || 1} />
            ))}
            {agg.top_countries.length === 0 && <p className="text-xs text-ash">Aucune donnée.</p>}
          </div>
        </div>
        <div className="bg-white border border-stone rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ash mb-3 flex items-center gap-1"><Monitor className="h-3 w-3" /> Appareils · Navigateurs</p>
          <div className="space-y-2">
            {agg.top_devices.slice(0, 3).map((d) => (
              <TinyBar key={d.key} label={d.key} count={d.count} total={total || 1} />
            ))}
            {agg.top_browsers?.slice(0, 3).map((b) => (
              <TinyBar key={`b-${b.key}`} label={b.key} count={b.count} total={total || 1} />
            ))}
            {agg.top_devices.length === 0 && <p className="text-xs text-ash">Aucune donnée.</p>}
          </div>
        </div>
        <div className="bg-white border border-stone rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ash mb-3 flex items-center gap-1"><Route className="h-3 w-3" /> Top sources</p>
          <div className="space-y-2">
            {agg.top_referrers.slice(0, 5).map((r) => (
              <TinyBar key={r.key} label={r.key || "direct"} count={r.count} total={total || 1} />
            ))}
            {agg.top_referrers.length === 0 && <p className="text-xs text-ash">Aucune donnée.</p>}
          </div>
        </div>
      </div>

      {/* Filters card */}
      <div className="bg-white border border-stone rounded-sm p-5 mb-6" data-testid="filters-card">
        <form onSubmit={applyFilters} className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-ash" />
            <span className="text-xs uppercase tracking-[0.14em] text-ink font-medium">Filtres</span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-xs uppercase tracking-[0.1em] text-ash hover:text-ink flex items-center gap-1"
            >
              {showAdvanced ? <><ChevronUp className="h-3 w-3" /> Moins</> : <><ChevronDown className="h-3 w-3" /> Filtres avancés</>}
            </button>
          </div>

          {/* Filtres de base */}
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Période</label>
              <select
                data-testid="f-days"
                value={filters.days}
                onChange={(e) => setFilters((f) => ({ ...f, days: Number(e.target.value) }))}
                className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
              >
                {DAYS_PRESETS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Conversion</label>
              <select
                data-testid="f-conv"
                value={filters.conv}
                onChange={(e) => setFilters((f) => ({ ...f, conv: e.target.value }))}
                className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
              >
                <option value="all">Tous</option>
                <option value="yes">Convertis</option>
                <option value="no">Non convertis</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Robots</label>
              <select
                data-testid="f-bots"
                value={filters.bots}
                onChange={(e) => setFilters((f) => ({ ...f, bots: e.target.value }))}
                className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
                title="Google/Bing vérifiés, imposteurs et autres robots sont exclus des visiteurs par défaut"
              >
                <option value="exclude">Humains seulement{data?.bots_total ? ` (${data.bots_total} robots masqués)` : ""}</option>
                <option value="only">Robots uniquement</option>
                <option value="all">Humains + robots</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Pays</label>
              <select
                data-testid="f-country"
                value={filters.country}
                onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
              >
                <option value="">Tous</option>
                <CountryOptions />
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Recherche</label>
              <div className="relative mt-1">
                <Search className="h-3.5 w-3.5 text-ash absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="f-search"
                  type="text"
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  placeholder="IP, ville, email, referrer…"
                  className="w-full border border-stone rounded-sm pl-8 pr-3 py-2 text-sm bg-white"
                />
              </div>
            </div>
          </div>

          {/* Filtres avancés */}
          {showAdvanced && (
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-stone">
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Appareil</label>
                <select
                  data-testid="f-device"
                  value={filters.device}
                  onChange={(e) => setFilters((f) => ({ ...f, device: e.target.value }))}
                  className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
                >
                  <option value="">Tous</option>
                  {DEVICE_PRESETS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Navigateur</label>
                <select
                  data-testid="f-browser"
                  value={filters.browser}
                  onChange={(e) => setFilters((f) => ({ ...f, browser: e.target.value }))}
                  className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
                >
                  <option value="">Tous</option>
                  {BROWSER_PRESETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Système</label>
                <select
                  data-testid="f-os"
                  value={filters.os}
                  onChange={(e) => setFilters((f) => ({ ...f, os: e.target.value }))}
                  className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
                >
                  <option value="">Tous</option>
                  {OS_PRESETS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Comportement</label>
                <div className="flex gap-1.5 mt-1">
                  <select
                    data-testid="f-has-cart"
                    value={filters.has_cart}
                    onChange={(e) => setFilters((f) => ({ ...f, has_cart: e.target.value }))}
                    className="flex-1 border border-stone rounded-sm px-2 py-2 text-xs bg-white"
                    title="Ajout panier"
                  >
                    <option value="any">Panier : tous</option>
                    <option value="yes">Avec panier</option>
                    <option value="no">Sans panier</option>
                  </select>
                  <select
                    data-testid="f-has-products"
                    value={filters.has_products}
                    onChange={(e) => setFilters((f) => ({ ...f, has_products: e.target.value }))}
                    className="flex-1 border border-stone rounded-sm px-2 py-2 text-xs bg-white"
                    title="A consulté des produits"
                  >
                    <option value="any">Produits : tous</option>
                    <option value="yes">Vu des produits</option>
                    <option value="no">Aucun produit</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Pages min</label>
                <input
                  data-testid="f-min-pages"
                  type="number"
                  min="0"
                  value={filters.min_pages}
                  onChange={(e) => setFilters((f) => ({ ...f, min_pages: e.target.value }))}
                  placeholder="Ex: 3"
                  className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-ash">Durée min (s)</label>
                <input
                  data-testid="f-min-duration"
                  type="number"
                  min="0"
                  value={filters.min_duration_sec}
                  onChange={(e) => setFilters((f) => ({ ...f, min_duration_sec: e.target.value }))}
                  placeholder="Ex: 30"
                  className="w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white"
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-stone">
            <button
              type="submit"
              data-testid="apply-filters"
              className="bg-ink text-cream px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors rounded-sm"
            >
              Appliquer
            </button>
            <button
              type="button"
              onClick={resetFilters}
              data-testid="reset-filters"
              className="border border-stone text-graphite px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-sand transition-colors rounded-sm flex items-center gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
            </button>
            <div className="flex-1" />
            <span className="text-xs text-ash">
              {total > 0 ? <>Affiche {pageStart}–{pageEnd} sur {total}</> : <>Aucun résultat</>}
            </span>
          </div>
        </form>

        {/* Chips */}
        {activeChips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-3 mt-3 border-t border-stone">
            <span className="text-[10px] uppercase tracking-[0.14em] text-ash">Filtres actifs :</span>
            {activeChips.map((c) => (
              <button
                key={c.k}
                onClick={() => removeChip(c.k)}
                className="inline-flex items-center gap-1 bg-sand border border-stone px-2 py-0.5 text-[11px] text-graphite hover:bg-stone/40 rounded-sm"
              >
                {c.label}
                <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone rounded-sm" data-testid="visitors-table-card">
        {err ? (
          <p className="text-destructive p-6">Erreur de chargement des visiteurs organiques.</p>
        ) : !data && loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-12 bg-sand animate-pulse" />)}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-stone mx-auto mb-3" strokeWidth={1.2} />
            <p className="text-graphite">Aucun visiteur organique ne correspond à ces filtres.</p>
            <p className="text-xs text-ash mt-1">Essayez d&apos;élargir la période ou de réinitialiser.</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm" data-testid="organic-visitors-table">
              <thead className="bg-sand/60">
                <tr className="border-b border-stone">
                  <th className="py-3 pr-3 pl-4 text-left">
                    <SortHeader label="Visiteur" field="first_seen" sort={applied.sort} order={applied.order} onSort={onSort} />
                  </th>
                  <th className="py-3 px-2 text-left"><span className="text-xs uppercase tracking-[0.12em] text-ash">IP</span></th>
                  <th className="py-3 px-2 text-left"><span className="text-xs uppercase tracking-[0.12em] text-ash">Localisation</span></th>
                  <th className="py-3 px-2 text-left"><span className="text-xs uppercase tracking-[0.12em] text-ash">Appareil</span></th>
                  <th className="py-3 px-2 text-left"><span className="text-xs uppercase tracking-[0.12em] text-ash">Source</span></th>
                  <th className="py-3 px-2 text-right">
                    <SortHeader label="Pages" field="page_count" sort={applied.sort} order={applied.order} onSort={onSort} align="right" />
                  </th>
                  <th className="py-3 px-2 text-right">
                    <SortHeader label="Durée" field="duration_ms" sort={applied.sort} order={applied.order} onSort={onSort} align="right" />
                  </th>
                  <th className="py-3 px-2 text-right">
                    <SortHeader label="Risque" field="risk" sort={applied.sort} order={applied.order} onSort={onSort} align="right" />
                  </th>
                  <th className="py-3 px-2 text-left"><span className="text-xs uppercase tracking-[0.12em] text-ash">Statut</span></th>
                  <th className="py-3 pl-2 pr-4 text-right">
                    <SortHeader label="Vu à" field="last_seen" sort={applied.sort} order={applied.order} onSort={onSort} align="right" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {list.map((v) => (
                  <tr
                    key={v.session_id}
                    onClick={() => setSelectedVisitor(v.session_id)}
                    className={`cursor-pointer transition-colors ${v.is_blocked ? "bg-red-50/50 hover:bg-red-50" : "hover:bg-sand/50"}`}
                    data-testid="organic-visitor-row"
                  >
                    <td className="py-3 pr-3 pl-4">
                      <p className="text-ink font-medium text-sm">{v.user_email || <span className="text-ash italic">Anonyme</span>}</p>
                      <p className="text-[11px] text-ash font-mono truncate max-w-[140px]" title={v.session_id}>{v.session_id.slice(0, 14)}…</p>
                    </td>
                    <td className={`py-3 px-2 text-graphite font-mono text-xs ${v.multi_country ? "bg-amber-50/60" : v.multi_ip ? "bg-sky-50/50" : ""}`}>
                      <span className="inline-flex items-center gap-1" title={v.multi_ip ? `IP actuelle : ${v.last_ip || v.ip} · première : ${v.ip}` : undefined}>
                        <Wifi className="h-3 w-3 text-ash" /> {v.multi_ip ? (v.last_ip || v.ip) : (v.ip || "—")}
                      </span>
                      <div className="mt-1 font-sans flex flex-wrap gap-1"><RiskPill risk={v.risk} compact /><MobilityBadge v={v} compact /><BlockedBadge v={v} compact /><BotBadge v={v} compact /></div>
                    </td>
                    <td className="py-3 px-2 text-graphite text-xs">
                      <p className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-ash" />
                        <span>{flagEmoji(v.country_code)} {v.city ? `${v.city}, ` : ""}{v.country || "—"}</span>
                      </p>
                      {v.multi_country ? (
                        <p className="text-amber-800 text-[11px] mt-0.5 pl-4" title={`Pays vus pour cet appareil : ${v.countries.join(", ")}`}>
                          {v.countries.slice(0, 2).map((c) => `${flagEmoji(c)} ${c}`).join(" → ")}{v.countries.length > 2 ? ` +${v.countries.length - 2} (détail parcours)` : ""}
                        </p>
                      ) : v.region && <p className="text-ash text-[11px] mt-0.5 pl-4">{v.region}</p>}
                    </td>
                    <td className="py-3 px-2 text-graphite text-xs">
                      <p className="flex items-center gap-1">
                        {v.device === "Mobile" ? <Smartphone className="h-3 w-3 text-ash" />
                          : v.device === "Tablette" ? <Tablet className="h-3 w-3 text-ash" />
                          : <Monitor className="h-3 w-3 text-ash" />}
                        <span>{v.device || "—"}</span>
                      </p>
                      <p className="text-ash text-[11px] mt-0.5 pl-4">{v.browser || "—"} · {v.os || "—"}</p>
                    </td>
                    <td className="py-3 px-2 text-graphite text-xs max-w-[180px]">
                      <p className="truncate" title={v.referrer || "Accès direct"}>
                        {v.referrer || <span className="text-ash italic">Direct</span>}
                      </p>
                      {v.last_path && <p className="text-ash text-[11px] mt-0.5 truncate">→ {v.last_path}</p>}
                    </td>
                    <td className="py-3 px-2 text-right text-graphite text-sm">{v.page_count}</td>
                    <td className="py-3 px-2 text-right text-graphite text-xs">{fmtDuration(v.duration_ms)}</td>
                    <td className="py-3 px-2 text-right"><RiskPill risk={v.risk} compact /></td>
                    <td className="py-3 px-2">
                      {v.converted ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-green-100 text-green-800 border border-green-200 rounded-sm whitespace-nowrap">
                          Converti · {eur(v.revenue)}
                        </span>
                      ) : v.cart_adds_count > 0 ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-copper/10 text-copper border border-copper/30 rounded-sm whitespace-nowrap">
                          Panier {v.cart_adds_count}
                        </span>
                      ) : v.products_count > 0 ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-gold/10 text-ink border border-gold/40 rounded-sm whitespace-nowrap">
                          Produits {v.products_count}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-stone/40 text-graphite border border-stone rounded-sm">Visite</span>
                      )}
                    </td>
                    <td className="py-3 pl-2 pr-4 text-right text-ash text-xs whitespace-nowrap">{fmtTime(v.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile : cartes (le tableau est masqué sous md) */}
          <div className="md:hidden divide-y divide-stone" data-testid="organic-visitors-cards">
            {list.map((v) => (
              <button
                key={v.session_id}
                type="button"
                onClick={() => setSelectedVisitor(v.session_id)}
                className={`w-full text-left p-4 space-y-2 ${v.is_blocked ? "bg-red-50/50" : v.multi_country ? "bg-amber-50/40" : "bg-white"} active:bg-sand/60`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{v.user_email || <span className="text-ash italic">Anonyme</span>}</p>
                    <p className="text-[11px] text-ash font-mono truncate" title={v.session_id}>{v.session_id.slice(0, 14)}…</p>
                  </div>
                  <span className="text-[11px] text-ash whitespace-nowrap">{fmtTime(v.last_seen)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-graphite">
                  <span className="inline-flex items-center gap-1 font-mono"><Wifi className="h-3 w-3 text-ash" /> {v.multi_ip ? (v.last_ip || v.ip) : (v.ip || "—")}</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-ash" /> {flagEmoji(v.country_code)} {v.city ? `${v.city}, ` : ""}{v.country || "—"}</span>
                  {v.multi_country && <span className="text-amber-800">{v.countries.slice(0, 2).map((c) => `${flagEmoji(c)} ${c}`).join(" → ")}{v.countries.length > 2 ? ` +${v.countries.length - 2}` : ""}</span>}
                </div>
                <div className="flex flex-wrap gap-1">
                  <RiskPill risk={v.risk} compact /><MobilityBadge v={v} compact /><BlockedBadge v={v} compact /><BotBadge v={v} compact />
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ash">
                  <span className="inline-flex items-center gap-1">
                    {v.device === "Mobile" ? <Smartphone className="h-3 w-3" /> : v.device === "Tablette" ? <Tablet className="h-3 w-3" /> : <Monitor className="h-3 w-3" />}
                    {v.device || "—"} · {v.browser || "—"} · {v.os || "—"}
                  </span>
                  <span>{v.page_count} pages · {fmtDuration(v.duration_ms)}</span>
                  <span className="truncate max-w-[200px]">{v.referrer ? `via ${v.referrer}` : "Accès direct"}{v.last_path ? ` → ${v.last_path}` : ""}</span>
                </div>
                <div>
                  {v.converted ? (
                    <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-green-100 text-green-800 border border-green-200 rounded-sm">Converti · {eur(v.revenue)}</span>
                  ) : v.cart_adds_count > 0 ? (
                    <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-copper/10 text-copper border border-copper/30 rounded-sm">Panier · {v.cart_adds_count}</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 text-[10px] uppercase bg-stone/40 text-graphite border border-stone rounded-sm">{v.products_count > 0 ? `${v.products_count} produit(s) vu(s)` : "Navigation"}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
          </>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-stone bg-sand/30">
            <div className="flex items-center gap-2">
              <span className="text-xs text-ash">Par page :</span>
              <select
                data-testid="page-size"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                className="border border-stone rounded-sm px-2 py-1 text-xs bg-white text-graphite"
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="page-prev"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className={`p-1.5 border border-stone rounded-sm ${canPrev ? "bg-white text-graphite hover:bg-sand" : "bg-sand text-stone cursor-not-allowed"}`}
                title="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-graphite px-3">
                Page <span className="font-medium text-ink">{page + 1}</span> / {totalPages}
              </span>
              <button
                data-testid="page-next"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
                className={`p-1.5 border border-stone rounded-sm ${canNext ? "bg-white text-graphite hover:bg-sand" : "bg-sand text-stone cursor-not-allowed"}`}
                title="Page suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <span className="text-xs text-ash">
              {pageStart}–{pageEnd} sur {total}
            </span>
          </div>
        )}
      </div>

      {selectedVisitor && (
        <VisitorJourney sessionId={selectedVisitor} onClose={() => setSelectedVisitor(null)} />
      )}

      </div>
      )}

      {activeTab === "ip-stats" && (
        <div>
          {/* Controls */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.12em] text-ash">Période :</span>
              {[1, 7, 14, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setIpStatsDays(d)}
                  className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${ipStatsDays === d ? "bg-ink text-cream border-ink" : "bg-white text-graphite border-stone hover:border-graphite"}`}
                >
                  {d === 1 ? "24h" : `${d} j`}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <select
                data-testid="ip-sort"
                value={ipSort}
                onChange={(e) => setIpSort(e.target.value)}
                className="border border-stone rounded-sm px-3 py-1.5 text-xs bg-white text-graphite"
                title="Tri"
              >
                <option value="recent">Plus récentes (en direct)</option>
                <option value="sessions">Plus actives</option>
              </select>
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-ash absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={ipSearchIp}
                  onChange={(e) => setIpSearchIp(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadIpStats()}
                  placeholder="Rechercher IP…"
                  className="border border-stone rounded-sm pl-8 pr-3 py-1.5 text-xs bg-white w-40"
                />
              </div>
              <select
                value={ipSearchCountry}
                onChange={(e) => setIpSearchCountry(e.target.value)}
                className="border border-stone rounded-sm px-3 py-1.5 text-xs bg-white text-graphite"
              >
                <option value="">Tous les pays</option>
                <CountryOptions />
              </select>
              <button
                onClick={loadIpStats}
                className="p-2 border border-stone bg-white text-graphite hover:bg-sand rounded-sm"
                title="Rafraîchir"
              >
                <RefreshCw className={`h-4 w-4 ${ipStatsLoading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Blocage manuel */}
          <div className="flex items-center gap-2 mb-5 p-4 bg-white border border-stone rounded-sm">
            <Ban className="h-4 w-4 text-red-500 shrink-0" />
            <span className="text-xs uppercase tracking-[0.12em] text-ash shrink-0">Bloquer une IP :</span>
            <input
              type="text"
              value={manualBlockIp}
              onChange={(e) => setManualBlockIp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && manualBlock()}
              placeholder="Ex : 160.177.76.250 ou 160.177.0.0/16 (plage)"
              className="border border-stone rounded-sm px-3 py-1.5 text-sm bg-white font-mono flex-1 max-w-xs"
            />
            <button
              onClick={manualBlock}
              disabled={!manualBlockIp.trim() || manualBlocking}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs uppercase tracking-[0.12em] bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 rounded-sm transition-colors"
            >
              <Ban className="h-3.5 w-3.5" />
              {manualBlocking ? "Blocage…" : "Bloquer"}
            </button>
          </div>

          {/* IP stats table */}
          <div className="bg-white border border-stone rounded-sm overflow-hidden">
            {ipStatsLoading && !ipStats ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-10 bg-sand animate-pulse rounded-sm" />)}
              </div>
            ) : !ipStats || ipStats.ips.length === 0 ? (
              <div className="text-center py-16">
                <Wifi className="h-10 w-10 text-stone mx-auto mb-3" strokeWidth={1.2} />
                <p className="text-graphite">Aucune donnée IP disponible pour cette période.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px] text-sm">
                  <thead className="bg-sand/60">
                    <tr className="border-b border-stone">
                      <th className="py-3 pl-4 pr-3 text-left text-xs uppercase tracking-[0.12em] text-ash">#</th>
                      <th className="py-3 px-3 text-left text-xs uppercase tracking-[0.12em] text-ash">Adresse IP</th>
                      <th className="py-3 px-3 text-right text-xs uppercase tracking-[0.12em] text-ash">Sessions</th>
                      <th className="py-3 px-3 text-right text-xs uppercase tracking-[0.12em] text-ash" title="Requêtes refusées par le blocage IP / pays sur la période">Refusées</th>
                      <th className="py-3 px-3 text-left text-xs uppercase tracking-[0.12em] text-ash">Pays</th>
                      <th className="py-3 px-3 text-left text-xs uppercase tracking-[0.12em] text-ash">Ville</th>
                      <th className="py-3 px-3 text-left text-xs uppercase tracking-[0.12em] text-ash">Dernière vue</th>
                      <th className="py-3 pl-3 pr-4 text-right text-xs uppercase tracking-[0.12em] text-ash">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone">
                    {ipStats.ips.map((row, idx) => (
                      <tr key={row.ip} className={`transition-colors ${row.is_blocked ? "bg-red-50/60" : "hover:bg-sand/40"}`}>
                        <td className="py-3 pl-4 pr-3 text-xs text-ash">{idx + 1}</td>
                        <td className="py-3 px-3">
                          <span className="font-mono text-sm text-ink">{row.ip}</span>
                          {row.bot_type && (
                            <span className="ml-2" title={row.bot_source === "refus" ? `Identifié d'après les requêtes refusées${row.refused_user_agent ? ` : ${row.refused_user_agent}` : ""}` : "Identifié d'après les visites trackées"}>
                              <BotBadge v={row} compact />
                            </span>
                          )}
                          {row.multi_ip && (
                            <div className="mt-1"><MobilityBadge v={{ ip: row.ip, linked_ips: row.linked_ips, linked_countries: row.linked_countries }} compact />
                              <span className="ml-1 text-[10px] text-ash">{row.nomad_devices} appareil{row.nomad_devices > 1 ? "s" : ""} vu{row.nomad_devices > 1 ? "s" : ""} aussi ailleurs</span>
                            </div>
                          )}
                          {row.is_blocked && (
                            <span
                              className="ml-2 inline-block px-1.5 py-0.5 text-[10px] uppercase bg-red-100 text-red-700 border border-red-200 rounded-sm"
                              title={row.blocked_at ? `Bloquée le ${fmtTime(row.blocked_at)}${row.blocked_reason ? ` — ${row.blocked_reason}` : ""}` : undefined}
                            >{row.blocked_by && row.blocked_by !== row.ip ? `Bloquée (${row.blocked_by})` : "Bloquée"}</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className={`font-semibold text-sm ${row.sessions >= 100 ? "text-red-600" : row.sessions >= 30 ? "text-copper" : "text-ink"}`}>
                            {row.sessions}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          {row.blocked_hits > 0 ? (
                            <span className="font-semibold text-sm text-red-600" title={row.last_blocked_at ? `Dernière tentative refusée : ${fmtTime(row.last_blocked_at)}` : undefined}>
                              {row.blocked_hits}
                            </span>
                          ) : (
                            <span className="text-xs text-ash">—</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-xs text-graphite">
                          {row.country_code ? `${flagEmoji(row.country_code)} ${row.country || row.country_code}` : "—"}
                        </td>
                        <td className="py-3 px-3 text-xs text-graphite">{row.city || "—"}</td>
                        <td className="py-3 px-3 text-xs text-ash whitespace-nowrap">
                          {row.last_activity ? fmtTime(row.last_activity) : "—"}
                          {row.last_path && <span className="block text-[10px] text-ash/80 truncate max-w-[160px]" title={row.last_path}>{row.last_path}</span>}
                        </td>
                        <td className="py-3 pl-3 pr-4 text-right">
                          <button
                            onClick={() => toggleIpBlock(row)}
                            disabled={blockingIp === row.ip}
                            title={row.is_blocked ? "Débloquer cette IP" : "Bloquer cette IP"}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors disabled:opacity-50 ${
                              row.is_blocked
                                ? "bg-white text-green-700 border-green-300 hover:bg-green-50"
                                : "bg-white text-red-600 border-red-200 hover:bg-red-50"
                            }`}
                          >
                            {row.is_blocked ? <ShieldOff className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                            {row.is_blocked ? "Débloquer" : "Bloquer"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-3 border-t border-stone bg-sand/30 text-xs text-ash">
                  {ipStats.ips.length} adresse{ipStats.ips.length > 1 ? "s" : ""} IP · {ipStatsDays === 1 ? "Dernières 24h" : `Derniers ${ipStatsDays} jours`}
                  {ipStats.generated_at && <> · actualisé à {fmtTime(ipStats.generated_at)}</>}
                  {" · "}<span className="text-copper">Rouge = ≥ 100 sessions · Orange = ≥ 30 sessions</span>
                  {" · "}<span className="text-red-600">Refusées = requêtes bloquées par le blocage IP / pays</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
